/**
 * Step 10 · Resource Classification (Measured / Indicated / Inferred).
 *
 * P18a fix (Feb 2026) — reads back the block model, tallies classification,
 * shows JORC-aligned table, AND enforces the "Cut-Off Too High" advisory
 * flow so a valid estimation with 0 ore blocks is no longer misreported as
 * a failed build.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  WarningCircle, TrendUp, ChartBar, ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, getBlockModel } from "@/lib/api";
import { WizardCard, StatusRow, KV, LoadingBanner, Pill } from "@/pages/wizard/_shared";
import GradeTonnagePanel from "@/pages/wizard/GradeTonnagePanel";
import PseudoPitPanel from "@/pages/wizard/PseudoPitPanel";
import ResourceStatementPanel from "@/pages/wizard/ResourceStatementPanel";
import MineDesignWorkspace from "@/pages/wizard/MineDesignWorkspace";
import SectionEnginePanel from "@/pages/wizard/SectionEnginePanel";
import P28WorkspacePanel from "@/pages/wizard/P28WorkspacePanel";


const CLASSES = ["Measured", "Indicated", "Inferred", "Unclassified"];
const CLASS_TONE = {
  Measured: "green",
  Indicated: "blue",
  Inferred: "amber",
  Unclassified: "slate",
};


// ── Cut-Off Too High advisory ────────────────────────────────────────
function CutoffAdvisory({ selectedCutoff, maxGrade, medianGrade, recommendedCutoff, unit,
                          totalBlocks, oreCount, wasteCount,
                          onUseRecommended, onEnterNew, onContinue, onViewDistribution }) {
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4 space-y-3"
          data-testid="w-classify-cutoff-warning">
      <div className="flex items-start gap-2">
        <WarningCircle size={22} weight="fill" className="text-amber-600 shrink-0" />
        <div className="flex-1">
          <div className="text-[15px] font-bold text-amber-900" data-testid="w-classify-cutoff-warning-title">
            Cut-Off Too High — No Ore Blocks
          </div>
          <div className="text-[12px] text-amber-800 mt-1">
            The selected cut-off grade of <b>{selectedCutoff} {unit}</b> is higher than the maximum
            estimated grade of <b>{Number.isFinite(maxGrade) ? maxGrade.toFixed(3) : "—"} {unit}</b>.
            No blocks currently qualify as ore.
          </div>
          <div className="text-[11px] text-amber-800 mt-1">
            This does <b>not</b> indicate an estimation failure. Review the cut-off or continue
            with zero ore blocks if the cut-off is intentional.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Current cut-off</div>
          <div className="text-slate-900 font-bold text-[13px]">{selectedCutoff} {unit}</div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Max estimated grade</div>
          <div className="text-slate-900 font-bold text-[13px]" data-testid="w-classify-max-grade">
            {Number.isFinite(maxGrade) ? maxGrade.toFixed(3) : "—"} {unit}
          </div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Median grade</div>
          <div className="text-slate-900 font-bold text-[13px]">
            {Number.isFinite(medianGrade) ? medianGrade.toFixed(3) : "—"} {unit}
          </div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Recommended range</div>
          <div className="text-slate-900 font-bold text-[13px]">
            {Number.isFinite(recommendedCutoff)
              ? `${(recommendedCutoff * 0.5).toFixed(3)} – ${recommendedCutoff.toFixed(3)}` : "—"}
          </div>
        </div>

        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Total estimated</div>
          <div className="text-slate-900 font-bold text-[13px]">{totalBlocks.toLocaleString()}</div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Ore blocks</div>
          <div className="text-slate-900 font-bold text-[13px]" data-testid="w-classify-ore-count">{oreCount.toLocaleString()}</div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Waste blocks</div>
          <div className="text-slate-900 font-bold text-[13px]">{wasteCount.toLocaleString()}</div>
        </div>
        <div className="bg-white/60 rounded border border-amber-200 p-2">
          <div className="text-amber-700 uppercase text-[9px] tracking-widest">Status</div>
          <div className="text-amber-900 font-bold text-[13px]">Warning (not Failed)</div>
        </div>
      </div>

      <div className="text-[10px] text-amber-800 italic">
        Preliminary Cut-Off Review — Not an Economic Optimization. Recommended cut-off is not applied
        automatically; explicit approval is required.
      </div>

      <div className="flex flex-wrap gap-2">
        {Number.isFinite(recommendedCutoff) && (
          <Button size="sm" variant="outline" className="h-8 text-[11px] border-amber-400 text-amber-900"
                   onClick={onUseRecommended} data-testid="w-classify-use-recommended">
            Use recommended {recommendedCutoff.toFixed(3)} {unit}
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-[11px]"
                   onClick={onEnterNew} data-testid="w-classify-enter-new">
          Enter new cut-off
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-[11px]"
                   onClick={onContinue} data-testid="w-classify-continue-zero">
          Continue with zero ore blocks
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-[11px]"
                   onClick={onViewDistribution} data-testid="w-classify-view-distribution">
          <ChartBar size={11} className="mr-1" /> View grade distribution
        </Button>
      </div>
    </div>
  );
}


export default function ClassificationStep({ projectId, project }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["wizard-classification", projectId],
    queryFn: () => getBlockModel(projectId),
    enabled: !!projectId,
    retry: false,
  });
  const [manualCutoff, setManualCutoff] = useState("");
  const [ackZero, setAckZero] = useState(false);
  const [showDistribution, setShowDistribution] = useState(false);

  const updateCutoffMut = useMutation({
    mutationFn: async (cutoff) => api.patch(`/projects/${projectId}`, { cutoff_grade: cutoff }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Cut-off updated. Rebuild the block model to apply.");
      qc.invalidateQueries({ queryKey: ["wizard-project", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.message || "Failed to update cut-off"),
  });

  if (q.isLoading) return <LoadingBanner label="Loading block model…" />;
  const bm = q.data;

  // ── Derive everything WITHOUT hooks so early return below is safe.
  const ore   = bm?.ore_blocks   || [];
  const waste = bm?.waste_blocks || [];
  const s     = bm?.summary || {};
  const totalBlocks = (s.total_blocks ?? (ore.length + waste.length)) || 0;
  const oreCount   = s.ore_blocks   ?? ore.length;
  const wasteCount = s.waste_blocks ?? waste.length;

  // ── Grade distribution stats from all estimated blocks (ore + waste)
  // Kept as plain sync compute rather than useMemo so we can early-return
  // above without breaking React's rules-of-hooks contract.
  const allGrades = [...ore, ...waste]
    .map((b) => Number(b.grade))
    .filter((g) => Number.isFinite(g));
  const maxGrade = allGrades.length ? Math.max(...allGrades) : NaN;
  const sortedGrades = allGrades.length ? [...allGrades].sort((a, b) => a - b) : [];
  const medianGrade = sortedGrades.length ? sortedGrades[Math.floor(sortedGrades.length / 2)] : NaN;

  if (!bm) {
    return (
      <StatusRow tone="warn">
        No block model yet — complete step 9 first.
      </StatusRow>
    );
  }
  const unit = s.grade_symbol || (project?.commodity === "Au" || project?.commodity === "Ag" ? "g/t" : "%");
  const selectedCutoff = Number(s.cutoff_grade ?? project?.cutoff_grade ?? 0);
  const recommendedCutoff = Number.isFinite(medianGrade)
    ? Math.max(0.05, +(medianGrade * 0.5).toFixed(3))
    : NaN;

  // ── Status classification per P18a spec ─────────────────────────
  const hasEstimatedBlocks = totalBlocks > 0;
  const hasOreBlocks       = oreCount > 0;
  const cutoffAboveMax     = Number.isFinite(maxGrade) && Number.isFinite(selectedCutoff)
                             && selectedCutoff > maxGrade;
  const cutoffInvalid      = !Number.isFinite(selectedCutoff) || selectedCutoff < 0;

  let status = { key: "pass",  tone: "green", label: "PASS" };
  if (!hasEstimatedBlocks || cutoffInvalid) {
    status = { key: "blocking", tone: "red",   label: "BLOCKING" };
  } else if (!hasOreBlocks || cutoffAboveMax) {
    status = { key: "warning",  tone: "amber", label: "WARNING" };
  }

  // Classification tallies
  const counts = { Measured: 0, Indicated: 0, Inferred: 0, Unclassified: 0 };
  const tonnes = { Measured: 0, Indicated: 0, Inferred: 0, Unclassified: 0 };
  const grade  = { Measured: 0, Indicated: 0, Inferred: 0, Unclassified: 0 };
  for (const b of ore) {
    const c = b.classification || "Unclassified";
    counts[c] = (counts[c] || 0) + 1;
    tonnes[c] += Number(b.tonnes || 0);
    grade[c]  += Number(b.tonnes || 0) * Number(b.grade || 0);
  }
  const totalTraw = Object.values(tonnes).reduce((a, b) => a + b, 0);
  const totalT = totalTraw || 1;   // divide-by-zero guard for weighted mean
  const totalG = ore.reduce((a, b) => a + (b.tonnes || 0) * (b.grade || 0), 0);
  const overallGrade = totalT > 0 && totalTraw > 0 ? totalG / totalT : 0;

  const showCutoffWarning = (cutoffAboveMax || (!hasOreBlocks && hasEstimatedBlocks)) && !ackZero;

  return (
    <div className="max-w-5xl space-y-4">
      {/* Overall status row */}
      <StatusRow tone={status.key === "blocking" ? "error" : status.key === "warning" ? "warn" : "ok"}>
        Estimation status:{" "}
        <Pill tone={status.tone} testId="w-classify-status">{status.label}</Pill>
        {" · "}
        <b>{totalBlocks.toLocaleString()}</b> estimated blocks,{" "}
        <b>{oreCount.toLocaleString()}</b> ore, <b>{wasteCount.toLocaleString()}</b> waste
        {" · max grade "}<b>{Number.isFinite(maxGrade) ? maxGrade.toFixed(3) : "—"}</b> {unit}
      </StatusRow>

      {showCutoffWarning && (
        <CutoffAdvisory
          selectedCutoff={selectedCutoff} maxGrade={maxGrade} medianGrade={medianGrade}
          recommendedCutoff={recommendedCutoff} unit={unit}
          totalBlocks={totalBlocks} oreCount={oreCount} wasteCount={wasteCount}
          onUseRecommended={() => {
            if (Number.isFinite(recommendedCutoff))
              updateCutoffMut.mutate(recommendedCutoff);
          }}
          onEnterNew={() => {
            const el = document.querySelector('[data-testid="w-classify-cutoff-input"]');
            el?.focus();
          }}
          onContinue={() => { setAckZero(true); toast.info("Continuing with zero ore blocks — recorded as WARNING"); }}
          onViewDistribution={() => setShowDistribution((v) => !v)} />
      )}

      {/* Manual cut-off input (Enter New) */}
      {showCutoffWarning && (
        <WizardCard title="Enter new cut-off" testId="w-classify-cutoff-editor">
          <div className="flex items-end gap-2">
            <div className="flex-1 max-w-[220px]">
              <Label className="text-[10px] font-semibold text-slate-600">New cut-off ({unit})</Label>
              <Input type="number" step="0.01"
                       value={manualCutoff}
                       onChange={(e) => setManualCutoff(e.target.value)}
                       placeholder={selectedCutoff.toString()}
                       className="h-8 text-[12px] font-mono"
                       data-testid="w-classify-cutoff-input" />
            </div>
            <Button size="sm"
                       onClick={() => {
                         const v = Number(manualCutoff);
                         if (!Number.isFinite(v) || v < 0) return toast.error("Cut-off must be ≥ 0");
                         updateCutoffMut.mutate(v);
                       }}
                       disabled={!manualCutoff || updateCutoffMut.isPending}
                       className="bg-brand-blue hover:bg-brand-blue/90 text-white h-8 text-[11px]"
                       data-testid="w-classify-cutoff-apply">
              <ArrowClockwise size={11} className="mr-1" /> Apply cut-off
            </Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Applying a new cut-off updates <code>project.cutoff_grade</code>.
            Rebuild the block model in Step 9 for the new cut-off to take effect on the ore/waste split.
          </div>
        </WizardCard>
      )}

      {/* Grade distribution mini-chart */}
      {showDistribution && Number.isFinite(maxGrade) && (
        <WizardCard title="Grade distribution" testId="w-classify-distribution"
                     subtitle={`${allGrades.length.toLocaleString()} estimated blocks`}>
          <GradeHistogram grades={allGrades} maxGrade={maxGrade} cutoff={selectedCutoff} unit={unit} />
        </WizardCard>
      )}

      {/* P0 · Mineral Resource Statement (A: uncut model + B: reportable at cutoff) */}
      {hasEstimatedBlocks && (
        <ResourceStatementPanel projectId={projectId}
                                  cutoffCurrent={selectedCutoff}
                                  maxGrade={maxGrade}
                                  unit={unit} />
      )}

      <WizardCard title="Resource statement (legacy ore-only breakdown)"
                   subtitle="Ore blocks only — see 'Mineral Resource Statement' above for the full uncut model."
                   testId="w-classify-table">
        <div className="table-wrapper">
          <table className="w-full text-[12px] font-mono">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="text-left py-1">Class</th>
                <th className="text-right py-1">Blocks</th>
                <th className="text-right py-1">Tonnes (t)</th>
                <th className="text-right py-1">Grade ({unit})</th>
                <th className="text-right py-1">Contained metal</th>
              </tr>
            </thead>
            <tbody>
              {CLASSES.map((c) => {
                const t = tonnes[c] || 0;
                const g = t > 0 ? (grade[c] / t) : 0;
                const cm = t * g;
                return (
                  <tr key={c} className="border-b border-slate-100"
                       data-testid={`w-classify-row-${c.toLowerCase()}`}>
                    <td className="py-1"><Pill tone={CLASS_TONE[c]}>{c.toUpperCase()}</Pill></td>
                    <td className="text-right">{counts[c]?.toLocaleString?.() ?? 0}</td>
                    <td className="text-right">{t.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td className="text-right">{g ? g.toFixed(3) : "—"}</td>
                    <td className="text-right">{cm ? cm.toFixed(1) : "—"}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-slate-300 font-bold">
                <td className="py-1">Total</td>
                <td className="text-right">{oreCount.toLocaleString()}</td>
                <td className="text-right">{totalTraw.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="text-right">{overallGrade ? overallGrade.toFixed(3) : "—"}</td>
                <td className="text-right">{totalG ? totalG.toFixed(1) : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </WizardCard>

      <WizardCard title="Anti-continuity guards" testId="w-classify-continuity">
        <KV k="Measured range"  v={`${s?.continuity_caps?.measured ?? "—"} m`} />
        <KV k="Indicated range" v={`${s?.continuity_caps?.indicated ?? "—"} m`} />
        <KV k="Inferred range"  v={`${s?.continuity_caps?.inferred ?? "—"} m`} />
      </WizardCard>

      {/* Grade-Tonnage curve — always shown for a valid block model. */}
      {hasEstimatedBlocks && (
        <GradeTonnagePanel projectId={projectId}
                            cutoffCurrent={selectedCutoff}
                            unit={unit} />
      )}

      {/* Conceptual pseudo-pit — visualization only. */}
      {hasEstimatedBlocks && hasOreBlocks && (
        <PseudoPitPanel projectId={projectId} />
      )}

      {/* Sprint P19.2 · Engineering Mine Design (Pit + Underground). */}
      {hasEstimatedBlocks && hasOreBlocks && (
        <MineDesignWorkspace projectId={projectId} />
      )}

      {/* Sprint P27 · Professional Section Engine */}
      <SectionEnginePanel projectId={projectId} />

      {/* Sprint P28 · Enterprise workspace (Validation · Classification · QAQC · DEM · Bundle) */}
      <P28WorkspacePanel projectId={projectId} />
    </div>
  );
}


// ── Simple SVG histogram (no chart dep) ──────────────────────────────
function GradeHistogram({ grades, maxGrade, cutoff, unit }) {
  const bins = 20;
  const step = maxGrade / bins || 0.01;
  const buckets = new Array(bins).fill(0);
  for (const g of grades) {
    const idx = Math.min(bins - 1, Math.max(0, Math.floor(g / step)));
    buckets[idx]++;
  }
  const maxCount = Math.max(...buckets, 1);
  const w = 640, h = 140, pad = 20;
  const bw = (w - pad * 2) / bins;
  const cutoffFrac = cutoff / (maxGrade || 1);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
      {buckets.map((c, i) => {
        const bh = (c / maxCount) * (h - pad * 2);
        const isOre = i * step >= cutoff;
        return (
          <rect key={i}
                 x={pad + i * bw + 1}
                 y={h - pad - bh}
                 width={bw - 2}
                 height={bh}
                 fill={isOre ? "#10B981" : "#94A3B8"} />
        );
      })}
      {/* Cutoff marker */}
      {cutoffFrac >= 0 && cutoffFrac <= 1 && (
        <g>
          <line x1={pad + cutoffFrac * (w - pad * 2)}
                 y1={pad}
                 x2={pad + cutoffFrac * (w - pad * 2)}
                 y2={h - pad}
                 stroke="#F59E0B" strokeDasharray="4 4" strokeWidth="1.5" />
          <text x={pad + cutoffFrac * (w - pad * 2) + 4}
                 y={pad + 10}
                 fontSize="9" fill="#B45309"
                 fontFamily="monospace">
            cutoff {cutoff} {unit}
          </text>
        </g>
      )}
      <text x={pad} y={h - 4} fontSize="9" fill="#64748B" fontFamily="monospace">0</text>
      <text x={w - pad - 30} y={h - 4} fontSize="9" fill="#64748B" fontFamily="monospace">
        {maxGrade.toFixed(2)}
      </text>
    </svg>
  );
}
